import compact from 'lodash/compact';
import map from 'lodash/map';
import upperCase from 'lodash/upperCase';
import Cookies from 'react-cookies';
import { COOKIES } from '../brands/default/constants';
import {
  URLCLEARPAYCREATECHECKOUT,
  URLCLEARPAYSENDPAYMENT,
} from '../brands/default/services';
import axios from '../components/_generic/axiosSplunk/axiosSplunk';
import {
  getWalletAmount,
  getDeliveryOptionType,
  getOrderLinesPayload,
} from './general';
import httpCommonHeaders from './httpCommonHeaders';
import { formatDateForApi } from './productsService';
import { getUserLocationShortName } from './user';

const PAY_TIMEOUT = 40_000;
const sessionSource = Cookies.load(COOKIES.sessionSource);

const getItems = ({ basketProducts, orderSummary }) => {
  return basketProducts.map((product) => {
    return {
      categories: [[product.category]],
      imageUrl: product.imageUrl,
      name: product.title,
      pageUrl: product.url,
      price: {
        amount: product.price,
        currency: upperCase(orderSummary?.currency),
      },
      quantity: product.quantity,
      sku: null,
    };
  });
};

export const createCheckout = async (data) => {
  const headers = httpCommonHeaders();
  headers.ALT_PAYMENT_METHODS = 'GOOGLE_PAY,APPLE_PAY';
  try {
    const response = await axios({
      cache: false,
      data,
      headers,
      method: 'POST',
      url: `${URLCLEARPAYCREATECHECKOUT}`,
      withCredentials: true,
    });
    if (response?.data) {
      return response?.data;
    }

    return null;
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const preparePaymentData = async ({
  basketProducts,
  deviceFingerprintId,
  finalOrderAmount,
  isWalletApplied,
  orderSummary,
  promoCode,
  sessionId,
  token,
  basketDiscountForExistingVipUser,
  vipOptIn,
}) => {
  return {
    adminFeeApplied: basketProducts.some((product) => product.adminFee > 0),
    cashAmount: finalOrderAmount,
    clearpayCheckoutToken: token,
    cvv: null,
    deviceFingerprintId,
    expressBuy: false,
    location: getUserLocationShortName(),
    orderLines: compact(
      map(basketProducts, (product) => {
        if (product.quantity <= 0 || product.soldOut) return null;
        const deliveryOptionType = getDeliveryOptionType({
          orderSummary,
          product,
        });
        const orderLinesPayload = getOrderLinesPayload({
          deliveryOptionType,
          product,
        });

        if (product.numberOfNights)
          orderLinesPayload.numberOfNights = product.numberOfNights;

        if (product.travel) {
          orderLinesPayload.travel = product.travel;
        }

        if (product.checkInDate) {
          orderLinesPayload.calendarCheckInDate = formatDateForApi(
            product.checkInDate,
          );
        }
        if (product.checkOutDate) {
          orderLinesPayload.calendarCheckOutDate = formatDateForApi(
            product.checkOutDate,
          );
        }

        return orderLinesPayload;
      }),
    ),
    postageDiscountAmount: orderSummary.totalPostageDiscount || null,
    postageOnCheckoutRule: orderSummary.postageOnCheckoutRule || null,
    postageOnRedemption: orderSummary.postageOnRedemption || null,
    promoCode: promoCode.value,
    purchaseSource: sessionSource || null,
    sessionId,
    vipDiscountApplied:
      Boolean(basketDiscountForExistingVipUser) ||
      (orderSummary?.vipSummary?.vipOptInSubscription?.vipOptInBasketDiscount >
        0 &&
        vipOptIn === true),
    vipOptedIn: vipOptIn || false,
    walletAmount: await getWalletAmount(orderSummary),
    walletUsed: isWalletApplied,
  };
};

export const sendClearpayPayment = async (data) => {
  try {
    const response = await axios({
      cache: false,
      data,
      headers: httpCommonHeaders(),
      method: 'POST',
      timeout: PAY_TIMEOUT,
      url: URLCLEARPAYSENDPAYMENT,
      withCredentials: true,
    });
    if (response?.data) {
      return response.data;
    }

    return null;
  } catch (error) {
    console.error(error);

    return null;
  }
};

export const getCheckoutData = ({
  basketProducts,
  finalOrderAmount,
  orderSummary,
  userProfile,
}) => {
  return {
    amount: {
      amount: finalOrderAmount,
      currency: upperCase(orderSummary?.currency),
    },
    billing: {
      countryCode: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE || 'gb'),
      line1: userProfile.addressLine1,
      line2: userProfile.addressLine2,
      name: `${userProfile.firstName} ${userProfile.lastName}`,
      phoneNumber: null,
      postcode: userProfile.postcode,
      region: userProfile.town,
    },
    consumer: {
      email: userProfile.email,
      givenNames: userProfile.firstName,
      phoneNumber: null,
      surname: userProfile.lastName,
    },
    discounts: [
      {
        amount: {
          amount: (
            orderSummary.totalPromoCodeDiscount +
            orderSummary.totalWalletAmount +
            orderSummary.totalPostageDiscount
          ).toFixed(2),
          currency: upperCase(orderSummary?.currency),
        },
        displayName: 'Promo code applied',
      },
    ],
    items: getItems({ basketProducts, orderSummary }),
    merchant: {
      popupOrginUrl: window.location.href,
      redirectCancelUrl: window.location.href,
      redirectConfirmUrl: window.location.href,
    },
    shipping: {
      countryCode: upperCase(process.env.NEXT_PUBLIC_COUNTRY_CODE || 'gb'),
      line1: userProfile.addressLine1,
      line2: userProfile.addressLine2,
      name: `${userProfile.firstName} ${userProfile.lastName}`,
      phoneNumber: null,
      postcode: userProfile.postcode,
      region: userProfile.town,
    },
    shippingAmount: {
      amount: (
        orderSummary.totalPostageAmount + orderSummary.totalAdminFee
      ).toFixed(2),
      currency: upperCase(orderSummary?.currency),
    },
    taxAmount: {
      amount: 0,
      currency: upperCase(orderSummary?.currency),
    },
  };
};
